import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { Button } from "@/components/ui/button";
import NumberInput from "@/components/form/NumberInput";
import { toast } from "@/hooks/use-toast";
import {
  CreateLeaveBalanceOnboard,
  LeaveBalance,
  LeaveBalanceDTO,
} from "@/models/leave-balances.model";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { LeaveType } from "@/models/leave-types.model";

interface Props {
  setSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalDaysValues: Record<number, number>;
  setTotalDaysValues: Dispatch<SetStateAction<Record<number, number>>>;
  isSubmitting: boolean;
}

function LeaveAllowanceForm({
  isSubmitting,
  setSubmit,
  setPage,
  totalDaysValues,
  setTotalDaysValues,
}: Props) {
  const { leaveTypes, loading, error } = useLeaveTypes();

  const [err, setErr] = useState<string>("");
  const [localTotalDaysValues, setLocalTotalDaysValues] =
    useState<Record<number, number>>(totalDaysValues);


  useEffect(() => {
    if (leaveTypes.length > 0) {
      const initialValues: Record<number, number> = leaveTypes.reduce(
        (acc: any, leaveType: LeaveType) => {
          acc[leaveType.id] = NaN; // Use NaN or undefined to represent empty value
          return acc;
        },
        {} as Record<number, number>
      );
      setLocalTotalDaysValues(initialValues);
    }
  }, [leaveTypes]);

  useEffect(() => {
    console.log("checking...", localTotalDaysValues);
    if (isValid()) {
      setTotalDaysValues(localTotalDaysValues);
    }
  }, [localTotalDaysValues]);

  const isValid = () => {
    const relevantValues = Object.entries(localTotalDaysValues).filter(
      ([id]) => {
        const leaveType = leaveTypes.find((lt: LeaveType) => lt.id === Number(id));
        return leaveType?.name !== "Unpaid Leave";
      }
    );

    if (
      relevantValues.length <
      leaveTypes.filter((lt: LeaveType) => lt.name !== "Unpaid Leave").length ||
      relevantValues.some(([_, value]) => isNaN(value))
    ) {
      setErr("Please fill in for all leave types.");
      return false;
    }
    setErr("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    console.log("submitting...");
    e.preventDefault();
    if (!isValid()) {
      return;
    }

    // check if for leave name unpaid, the value is empty
    // if it is, create a leave balance with no limit
    // else, create a leave balance with the value

    setSubmit(true);
  };


  return (
    <form className="w-full h-full flex flex-col" onSubmit={handleSubmit}>
      <div className="h-5/6 overflow-y-auto">
        <div className="grid grid-cols-2 gap-2 mb-4">
          {leaveTypes
            .filter((lt: LeaveType) => lt.name !== "Unpaid Leave")
            .map((leaveType: LeaveType) => (
              <div key={leaveType.id} className="mb-4">
                <div className="flex gap-2 flex-col">
                  <NumberInput
                    label={leaveType.name}
                    value={localTotalDaysValues[leaveType.id]}
                    setValue={(value: number) =>
                      setLocalTotalDaysValues((prev) => ({
                        ...prev,
                        [leaveType.id]: value,
                      }))
                    }
                    min={0}
                    step={0.5}
                  />

                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="flex h-1/6 flex-col justify-center gap-2">
        {err && <div className="text-red-500 text-sm text-start w-full">{err}</div>}
        <div className="justify-between flex w-full">

          <Button
            type="button"
            variant="outline"
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              Object.entries(totalDaysValues).filter(([id]) => {
                const leaveType = leaveTypes.find((lt: LeaveType) => lt.id === Number(id));
                return leaveType?.name !== "Unpaid Leave";
              }).length < leaveTypes.filter((lt: LeaveType) => lt.name !== "Unpaid Leave").length
            }
          >
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Submit"}
          </Button>
        </div>


      </div>
    </form>
  );
}

export default LeaveAllowanceForm;
